import { FC, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, SxProps, useTheme } from '@mui/material';

import { AuthRole } from '@libs/types';
import { LogoLight, LogoDark, LogoLightStill, LogoDarkStill } from '@carcareers/assets';
import { routes, isIos } from '@carcareers/utils';
import { useAuth } from '@carcareers/hooks';

export interface LogoProps {
  isDarkMode?: boolean;
  isMobile?: boolean;
  isOpenDrawer?: boolean;
  role?: AuthRole;
  sx?: SxProps;
}

export const Logo: FC<LogoProps> = ({ role, sx, isMobile = false, isOpenDrawer = false, isDarkMode = false }) => {
  const navigate = useNavigate();
  const { session } = useAuth();
  const theme = useTheme();
  const [videoError, setVideoError] = useState(false);
  const isIosDevice = isIos();

  const logo = useMemo(() => {
    if (isDarkMode) {
      return LogoDark;
    }

    return theme.palette.mode === 'light' ? LogoLight : LogoDark;
  }, [theme.palette.mode]);

  const LogoFallback = useMemo(() => {
    return theme.palette.mode === 'light' ? LogoLightStill : LogoDarkStill;
  }, [theme.palette.mode]);

  const mobileStyles: SxProps = {
    display: isOpenDrawer ? 'none' : { xs: 'flex', md: 'none' },
    width: 177,
    m: 'auto',
    alignSelf: 'center',
    justifySelf: 'center',
  };

  const desktopStyles: SxProps = {
    display: { xs: 'none', md: 'flex' },
    width: 177,
    alignSelf: 'center',
    justifySelf: 'center',
  };

  let styles: SxProps = mobileStyles;
  if (!isMobile && !sx) {
    styles = desktopStyles;
  } else if (sx !== undefined) {
    styles = sx;
  }

  const goHome = () => {
    if (!role) {
      role = session.user?.role as AuthRole;
    }

    if (role === AuthRole.ADMIN) {
      navigate(`/${routes.ADMIN_HOME}`);
    } else if (role === AuthRole.EMPLOYER) {
      navigate(`/${routes.EMPLOYER_HOME}`);
    } else if (role === AuthRole.JOBSEEKER) {
      navigate(`/${routes.JOBSEEKER_HOME}`);
    } else {
      navigate(`${routes.HOME}`);
    }
  };

  if (window.location.pathname === `/${routes.JOBSEEKERS}` && isMobile) {
    return null;
  }

  return (
    <Box
      data-testid="logo"
      sx={{
        ...styles,
        cursor: 'pointer',
        '& .MuiSvgIcon-root': {
          fill: 'red',
        },
      }}
      onClick={goHome}
      role={isMobile ? 'logo-mobile' : 'logo-desktop'}
    >
      {videoError || isIosDevice ? (
        <LogoFallback />
      ) : (
        <Box
          component="video"
          autoPlay
          muted
          onError={() => setVideoError(true)}
          sx={{ width: '100%', height: 'auto' }}
        >
          <source src={logo} type="video/webm" />
        </Box>
      )}
    </Box>
  );
};

export default Logo;
